import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import TutorLayout from '../../components/tutor/TutorLayout';
import TutorProfileEditModal from '../../components/TutorProfileEditModal';
import { FaEdit, FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaCalendarAlt } from 'react-icons/fa';

/**
 * Página de perfil específica para tutores
 * 
 * Muestra solo información personal y datos necesarios para facturación,
 * sin información de futbolistas o sede que ya está vinculada a los futbolistas.
 */
const TutorProfile = () => {
  const { user, loading } = useAuth();
  const [showEditModal, setShowEditModal] = useState(false);
  const [profileData, setProfileData] = useState(null);

  useEffect(() => {
    if (user) {
      setProfileData(user);
    }
  }, [user]);

  const handleOpenEditModal = () => {
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
  };

  const handleProfileSaved = () => {
    // Recargar datos del usuario si es necesario
    window.location.reload();
  };

  if (loading || !profileData) {
    return (
      <TutorLayout title="Mi Perfil" subtitle="Cargando información...">
        <div className="loading-container" style={{ textAlign: 'center', padding: '50px' }}>
          <div className="spinner"></div>
          <p>Cargando perfil...</p>
        </div>
      </TutorLayout>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'No especificada';
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  return (
    <TutorLayout title="Mi Perfil" subtitle="Información personal y datos de facturación">
      <div className="profile-container">
        {/* Header del perfil */}
        <div className="profile-header-card" style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '30px',
          marginBottom: '30px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div className="profile-avatar" style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              overflow: 'hidden',
              border: '4px solid #2e7d32'
            }}>
              <img
                src={profileData.avatar || '/img/default-avatar.jpg'}
                alt={profileData.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={{ margin: '0 0 10px 0', color: '#2e7d32' }}>
                {profileData.name}
              </h2>
              <p style={{ margin: '0 0 15px 0', color: '#666', fontSize: '16px' }}>
                Tutor/Padre de familia
              </p>
              <button
                className="btn btn-primary"
                onClick={handleOpenEditModal}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <FaEdit />
                Editar Perfil
              </button>
            </div>
          </div>
        </div>

        {/* Información personal */}
        <div className="profile-section-card" style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '25px',
          marginBottom: '25px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef'
        }}>
          <h3 style={{
            margin: '0 0 20px 0',
            color: '#2e7d32',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <FaUser />
            Información Personal
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            <div className="info-item">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                <FaEnvelope style={{ color: '#666' }} />
                <strong>Email:</strong>
              </div>
              <p style={{ margin: '0', color: '#333' }}>{profileData.email}</p>
            </div>

            <div className="info-item">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                <FaPhone style={{ color: '#666' }} />
                <strong>Teléfono:</strong>
              </div>
              <p style={{ margin: '0', color: '#333' }}>
                {profileData.phone || 'No especificado'}
              </p>
            </div>

            <div className="info-item">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                <FaCalendarAlt style={{ color: '#666' }} />
                <strong>Fecha de nacimiento:</strong>
              </div>
              <p style={{ margin: '0', color: '#333' }}>
                {formatDate(profileData.birthDate)}
              </p>
            </div>

            <div className="info-item">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                <FaMapMarkerAlt style={{ color: '#666' }} />
                <strong>Dirección:</strong>
              </div>
              <p style={{ margin: '0', color: '#333' }}>
                {profileData.address?.street ? (
                  <>
                    {profileData.address.street}<br />
                    {profileData.address.city} {profileData.address.postalCode}<br />
                    {profileData.address.country || 'España'}
                  </>
                ) : (
                  'No especificada'
                )}
              </p>
            </div>
          </div>

          {profileData.bio && (
            <div className="info-item" style={{ marginTop: '20px' }}>
              <strong>Biografía:</strong>
              <p style={{ margin: '5px 0 0 0', color: '#333' }}>{profileData.bio}</p>
            </div>
          )}
        </div>

        {/* Información de contacto de emergencia */}
        {(profileData.emergencyContact?.name || profileData.emergencyContact?.phone) && (
          <div className="profile-section-card" style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '25px',
            marginBottom: '25px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            border: '1px solid #e9ecef'
          }}>
            <h3 style={{
              margin: '0 0 20px 0',
              color: '#2e7d32',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <FaPhone />
              Contacto de Emergencia
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              {profileData.emergencyContact?.name && (
                <div className="info-item">
                  <strong>Nombre:</strong>
                  <p style={{ margin: '5px 0 0 0', color: '#333' }}>
                    {profileData.emergencyContact.name}
                  </p>
                </div>
              )}

              {profileData.emergencyContact?.phone && (
                <div className="info-item">
                  <strong>Teléfono:</strong>
                  <p style={{ margin: '5px 0 0 0', color: '#333' }}>
                    {profileData.emergencyContact.phone}
                  </p>
                </div>
              )}

              {profileData.emergencyContact?.relationship && (
                <div className="info-item">
                  <strong>Relación:</strong>
                  <p style={{ margin: '5px 0 0 0', color: '#333' }}>
                    {profileData.emergencyContact.relationship}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Nota informativa */}
        <div style={{
          backgroundColor: '#e8f5e8',
          border: '1px solid #2e7d32',
          borderRadius: '8px',
          padding: '15px',
          marginTop: '20px'
        }}>
          <p style={{ margin: '0', color: '#2e7d32', fontSize: '14px' }}>
            <strong>Nota:</strong> La información de tus futbolistas (sede, equipo, división, etc.) 
            se gestiona individualmente desde la sección Mis Futbolistas. Este perfil contiene
            únicamente tu información personal y datos necesarios para facturación.
          </p>
        </div>
      </div>

      {/* Modal de edición */}
      {showEditModal && (
        <TutorProfileEditModal
          isOpen={showEditModal}
          onClose={handleCloseEditModal}
          onSaved={handleProfileSaved}
        />
      )}
    </TutorLayout>
  );
};

export default TutorProfile;
